"use strict";

exports.__esModule = true;
exports.CARD_PAD_Y = exports.CARD_PAD_X = exports.CARD_INSET = exports.CARD_HEADER_HEIGHT = exports.CARD_HEADER_GAP = exports.CARD_BORDER = void 0;
exports.ChartCard = ChartCard;
exports.SLIM_HEIGHT = exports.SHORT_CARD_HEIGHT = void 0;
exports.getChartCardInnerSize = getChartCardInnerSize;
exports.getChartCardInset = getChartCardInset;
exports.getChartCardPadding = getChartCardPadding;
exports.hasChartCardHeader = hasChartCardHeader;
exports.isLegacyDarkBackground = isLegacyDarkBackground;
var _react = _interopRequireWildcard(require("react"));
var _styledComponents = _interopRequireDefault(require("styled-components"));
var _polished = require("polished");
var _Theme = require("../../../Theme");
var _TileContext = require("../TileContext");
var _excluded = ["width", "height", "title", "subtitle", "unit", "legend", "actions", "backgroundColor", "children", "dataTestId"];
function _interopRequireDefault(e) { return e && e.__esModule ? e : { "default": e }; }
function _interopRequireWildcard(e, t) { if ("function" == typeof WeakMap) var r = new WeakMap(), n = new WeakMap(); return (_interopRequireWildcard = function _interopRequireWildcard(e, t) { if (!t && e && e.__esModule) return e; var o, i, f = { __proto__: null, "default": e }; if (null === e || "object" != typeof e && "function" != typeof e) return f; if (o = t ? n : r) { if (o.has(e)) return o.get(e); o.set(e, f); } for (var _t in e) "default" !== _t && {}.hasOwnProperty.call(e, _t) && ((i = (o = Object.defineProperty) && Object.getOwnPropertyDescriptor(e, _t)) && (i.get || i.set) ? o(f, _t, i) : f[_t] = e[_t]); return f; })(e, t); }
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (-1 !== e.indexOf(n)) continue; t[n] = r[n]; } return t; }
// Inside a device card the chart draws no card of its own: just the chart,
// with this much breathing room.
var FLAT_PAD = 4;

// Geometry shared with getChartCardInnerSize so widgets can size their chart
// to the space the card leaves. The inset is the visual gap between
// neighbouring widgets on a layout that places them edge to edge.
var CARD_INSET = exports.CARD_INSET = 4;
var CARD_BORDER = exports.CARD_BORDER = 0;
var CARD_PAD_X = exports.CARD_PAD_X = 16;
var CARD_PAD_Y = exports.CARD_PAD_Y = 14;
var CARD_HEADER_HEIGHT = exports.CARD_HEADER_HEIGHT = 26;
var CARD_HEADER_GAP = exports.CARD_HEADER_GAP = 8;

// Below these the header would eat the chart; the card goes headerless.
var MIN_HEIGHT_FOR_HEADER = 110;
var MIN_WIDTH_FOR_HEADER = 150;
// Below this a widget whose title says little (a data-item chart's "Data
// items") leaves the title out, so a chart with its own legend and axes keeps
// the room it had without a card. Widgets decide this in their card props.
var SHORT_CARD_HEIGHT = exports.SHORT_CARD_HEIGHT = 200;
// Below these the card tightens its padding so the chart keeps some room:
// a little under 160px, and under 120px (a stripe chart in a short report
// cell) down to a 2px inset and hairline padding, so the chart has the cell
// rather than the chrome.
var COMPACT_HEIGHT = 160;
var COMPACT_PAD_X = 10;
var COMPACT_PAD_Y = 6;
var SLIM_HEIGHT = exports.SLIM_HEIGHT = 120;
var SLIM_PAD_X = 8;
var SLIM_PAD_Y = 2;

// The gap a card keeps from its neighbours; a short card gives most of it up.
function getChartCardInset(height) {
  return height < SLIM_HEIGHT ? 2 : CARD_INSET;
}
function getChartCardPadding(height) {
  if (height < SLIM_HEIGHT) {
    return {
      x: SLIM_PAD_X,
      y: SLIM_PAD_Y
    };
  }
  return height < COMPACT_HEIGHT ? {
    x: COMPACT_PAD_X,
    y: COMPACT_PAD_Y
  } : {
    x: CARD_PAD_X,
    y: CARD_PAD_Y
  };
}

/**
 * Older chart widgets saved a black canvas background by default. In the
 * card design charts sit on the white card, so that legacy value is ignored.
 */
function isLegacyDarkBackground(color) {
  if (!color) {
    return false;
  }
  try {
    return (0, _polished.getLuminance)(color) < 0.02;
  } catch (e) {
    return false;
  }
}
function hasChartCardHeader(_ref) {
  var width = _ref.width,
    height = _ref.height,
    title = _ref.title,
    legend = _ref.legend,
    unit = _ref.unit;
  var wantsHeader = Boolean(title) || Boolean(unit) || Boolean(legend && legend.length > 0);
  return wantsHeader && height >= MIN_HEIGHT_FOR_HEADER && width >= MIN_WIDTH_FOR_HEADER;
}
function getChartCardInnerSize(_ref2) {
  var width = _ref2.width,
    height = _ref2.height,
    hasHeader = _ref2.hasHeader;
  var frame = 2 * (getChartCardInset(height) + CARD_BORDER);
  var pad = getChartCardPadding(height);
  var innerWidth = Math.max(0, width - frame - 2 * pad.x);
  var headerHeight = hasHeader ? CARD_HEADER_HEIGHT + CARD_HEADER_GAP : 0;
  var innerHeight = Math.max(0, height - frame - 2 * pad.y - headerHeight);
  return {
    width: innerWidth,
    height: innerHeight
  };
}
var Card = _styledComponents["default"].div.withConfig({
  displayName: "ChartCard__Card",
  componentId: "sc-p6wvfz-0"
})(["position:relative;box-sizing:border-box;background:", ";border:0;border-radius:", ";box-shadow:", ";color:", ";overflow:visible;"], function (props) {
  return props.$background || (0, _Theme.val)("ChartCardBackground");
}, (0, _Theme.val)("ChartCardRadius"), (0, _Theme.val)("CardShadow"), (0, _Theme.val)("ChartCardTitleColor"));
var Header = _styledComponents["default"].div.withConfig({
  displayName: "ChartCard__Header",
  componentId: "sc-p6wvfz-1"
})(["display:flex;align-items:center;gap:10px;height:", "px;margin-bottom:", "px;min-width:0;"], CARD_HEADER_HEIGHT, CARD_HEADER_GAP);
var Title = _styledComponents["default"].div.withConfig({
  displayName: "ChartCard__Title",
  componentId: "sc-p6wvfz-2"
})(["font-family:", ";font-size:14px;font-weight:600;letter-spacing:-0.01em;color:", ";white-space:nowrap;overflow:hidden;text-overflow:ellipsis;flex:0 1 auto;min-width:0;"], (0, _Theme.val)("FontFamilyHeading"), (0, _Theme.val)("ChartCardTitleColor"));
var Subtitle = _styledComponents["default"].span.withConfig({
  displayName: "ChartCard__Subtitle",
  componentId: "sc-p6wvfz-3"
})(["font-size:11.5px;font-weight:400;color:", ";margin-left:6px;"], (0, _Theme.val)("ChartCardSubtitleColor"));
var Chip = _styledComponents["default"].span.withConfig({
  displayName: "ChartCard__Chip",
  componentId: "sc-p6wvfz-4"
})(["flex:none;font-family:", ";font-size:11px;padding:2px 6px;border-radius:4px;background:", ";color:", ";"], (0, _Theme.val)("ChartFontFamilyMono"), (0, _Theme.val)("ChartCardChipBackground"), (0, _Theme.val)("ChartCardChipColor"));
var Spacer = _styledComponents["default"].div.withConfig({
  displayName: "ChartCard__Spacer",
  componentId: "sc-p6wvfz-5"
})(["flex:1 1 auto;min-width:0;"]);
var Legend = _styledComponents["default"].div.withConfig({
  displayName: "ChartCard__Legend",
  componentId: "sc-p6wvfz-6"
})(["display:flex;align-items:center;gap:11px;flex:0 1 auto;min-width:0;overflow:hidden;"]);
var LegendItem = _styledComponents["default"].span.withConfig({
  displayName: "ChartCard__LegendItem",
  componentId: "sc-p6wvfz-7"
})(["display:inline-flex;align-items:center;gap:5px;font-size:11px;color:", ";white-space:nowrap;"], (0, _Theme.val)("ChartLegendTextColor"));
var Swatch = _styledComponents["default"].span.withConfig({
  displayName: "ChartCard__Swatch",
  componentId: "sc-p6wvfz-8"
})(["flex:none;width:", "px;height:", "px;border-radius:2px;background:", ";"], function (props) {
  return props.$shape === "line" ? 14 : 9;
}, function (props) {
  return props.$shape === "line" ? 2 : 9;
}, function (props) {
  return props.$color;
});
var Actions = _styledComponents["default"].div.withConfig({
  displayName: "ChartCard__Actions",
  componentId: "sc-p6wvfz-9"
})(["flex:none;display:flex;align-items:center;gap:6px;"]);
var Body = _styledComponents["default"].div.withConfig({
  displayName: "ChartCard__Body",
  componentId: "sc-p6wvfz-10"
})(["position:relative;"]);

/**
 * The chart panel from the next-gen design: a white shadow card with a
 * title row (title, optional subtitle and unit chip) and the legend on the
 * right, with the chart below. Pass `children` as a function to receive the
 * width and height left for the chart.
 */
function ChartCard(props) {
  var width = props.width,
    height = props.height,
    title = props.title,
    subtitle = props.subtitle,
    unit = props.unit,
    _props$legend = props.legend,
    legend = _props$legend === void 0 ? [] : _props$legend,
    actions = props.actions,
    backgroundColor = props.backgroundColor,
    children = props.children,
    _props$dataTestId = props.dataTestId,
    dataTestId = _props$dataTestId === void 0 ? "ChartCard" : _props$dataTestId,
    extra = _objectWithoutPropertiesLoose(props, _excluded);
  var inDeviceCard = (0, _react.useContext)(_TileContext.DeviceCardContext) === true;
  if (inDeviceCard) {
    var flatInner = {
      width: Math.max(0, width - 2 * FLAT_PAD),
      height: Math.max(0, height - 2 * FLAT_PAD)
    };
    return /*#__PURE__*/_react["default"].createElement("div", _extends({
      "data-testid": dataTestId,
      style: {
        position: "relative",
        boxSizing: "border-box",
        width: width,
        height: height,
        padding: FLAT_PAD
      }
    }, extra), /*#__PURE__*/_react["default"].createElement(Body, {
      style: {
        width: flatInner.width,
        height: flatInner.height
      }
    }, typeof children === "function" ? children(flatInner) : children));
  }
  var hasHeader = hasChartCardHeader({
    width: width,
    height: height,
    title: title,
    legend: legend,
    unit: unit
  });
  var inner = getChartCardInnerSize({
    width: width,
    height: height,
    hasHeader: hasHeader
  });
  var pad = getChartCardPadding(height);
  var inset = getChartCardInset(height);
  var background = isLegacyDarkBackground(backgroundColor) ? undefined : backgroundColor;
  return /*#__PURE__*/_react["default"].createElement(Card, _extends({
    "data-testid": dataTestId,
    $background: background,
    style: {
      margin: inset,
      width: width - 2 * inset,
      height: height - 2 * inset,
      padding: pad.y + "px " + pad.x + "px"
    }
  }, extra), hasHeader && /*#__PURE__*/_react["default"].createElement(Header, {
    "data-testid": dataTestId + ".Header"
  }, title && /*#__PURE__*/_react["default"].createElement(Title, null, title, subtitle && /*#__PURE__*/_react["default"].createElement(Subtitle, null, "\xB7 ", subtitle)), unit && /*#__PURE__*/_react["default"].createElement(Chip, null, unit), /*#__PURE__*/_react["default"].createElement(Spacer, null), legend.length > 0 && /*#__PURE__*/_react["default"].createElement(Legend, {
    "data-testid": dataTestId + ".Legend"
  }, legend.map(function (item) {
    return /*#__PURE__*/_react["default"].createElement(LegendItem, {
      key: item.label
    }, /*#__PURE__*/_react["default"].createElement(Swatch, {
      $color: item.color,
      $shape: item.shape
    }), item.label);
  })), actions && /*#__PURE__*/_react["default"].createElement(Actions, null, actions)), /*#__PURE__*/_react["default"].createElement(Body, {
    style: {
      width: inner.width,
      height: inner.height
    }
  }, typeof children === "function" ? children(inner) : children));
}